import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Alert,
    InputAdornment,
    Grid,
    Paper,
    Typography,
    Tooltip,
    CircularProgress,
    Link
} from '@mui/material';
import {
    ContentCopy as ContentCopyIcon,
    Delete as DeleteIcon,
    PersonAdd as PersonAddIcon,
    HowToReg as HowToRegIcon,
    AddCircle as AddCircleIcon
} from '@mui/icons-material';
import { db } from '../firebase';
import {
    doc,
    setDoc,
    deleteDoc,
    Timestamp,
    collection,  
    query,       
    where,       
    getDocs,
    serverTimestamp
      
} from 'firebase/firestore';

function EmployeeManagement({ user, orgId, employees, setEmployees, loading, setLoading }) {
    const [inviteEmail, setInviteEmail] = useState("");
    const [message, setMessage] = useState("");
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [inviteLink, setInviteLink] = useState("");
    const [copySuccess, setCopySuccess] = useState("");
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
        open: false,
        employeeId: null,
        email: "",
        isPendingInvite: false
    });
    const [addEmployeeDialog, setAddEmployeeDialog] = useState(false);
    const [directAddMessage, setDirectAddMessage] = useState("");
    const [newEmployee, setNewEmployee] = useState({
        email: "",
        name: "",
        password: ""
    });

    const handleInvite = async () => {
        setMessage("");
        if (!inviteEmail || !inviteEmail.includes('@')) {
            setMessage("❌ Please enter a valid email address");
            return;
        }

        setLoading(true);
        try {
            const employeeId = `${inviteEmail.replace(/[@.]/g, "_")}`;
            await setDoc(doc(db, "organizations", orgId, "users", employeeId), {
                email: inviteEmail,
                role: "employee",
                name: "Pending Invite",
                invited: Timestamp.now(),
                invitedBy: user.email
            });

            // Generate and show invite link
            const baseUrl = window.location.origin;
            const inviteUrl = `${baseUrl}/invite/${orgId}/${encodeURIComponent(inviteEmail)}`;
            setInviteLink(inviteUrl);
            setInviteDialogOpen(true);

            setMessage(`✅ Invite created for ${inviteEmail}`);
            setInviteEmail("");

            // Refresh employee list
            const usersRef = collection(db, "organizations", orgId, "users");
            const q = query(usersRef, where("role", "==", "employee"));
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEmployees(data);
        } catch (err) {
            setMessage(`❌ ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(inviteLink)
            .then(() => {
                setCopySuccess("✓ Link copied!");
                setTimeout(() => setCopySuccess(""), 2000);
            })
            .catch(err => {
                console.error('Failed to copy: ', err);
                setCopySuccess("✗ Copy failed");
            });
    };

    const handleDeleteEmployee = async (employeeId, email) => {
        const isPendingInvite = employeeId.includes('_');
        setDeleteConfirmDialog({
            open: true,
            employeeId,
            email,
            isPendingInvite
        });
    };

    const confirmDeleteEmployee = async () => {
        setLoading(true);
        try {
            const userRef = doc(db, "organizations", orgId, "users", deleteConfirmDialog.employeeId);
            await deleteDoc(userRef);

            setMessage(`✅ Employee removed successfully`);
            // Refresh employee list
            const usersRef = collection(db, "organizations", orgId, "users");
            const q = query(usersRef, where("role", "==", "employee"));
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEmployees(data);
        } catch (err) {
            console.error("Error during deletion:", err);
            setMessage(`❌ Error: ${err.message}`);
        } finally {
            setLoading(false);
            setDeleteConfirmDialog({ open: false, employeeId: null, email: "" });
        }
    };

    const handleAddEmployee = async () => {
        setDirectAddMessage("");

        if (!newEmployee.email || !newEmployee.email.includes('@')) {
            setDirectAddMessage("❌ Please enter a valid email address");
            return;
        }

        if (!newEmployee.name) {
            setDirectAddMessage("❌ Please enter a name");
            return;
        }

        if (!newEmployee.password || newEmployee.password.length < 6) {
            setDirectAddMessage("❌ Password must be at least 6 characters");
            return;
        }

        setLoading(true);

        try {
            // Create Firebase Auth user first
            const { createUserWithEmailAndPassword } = await import("firebase/auth");
            const { auth } = await import("../firebase");

            const userCredential = await createUserWithEmailAndPassword(auth, newEmployee.email, newEmployee.password);
            const newUserId = userCredential.user.uid;

            // Create user document with real UID
            await setDoc(doc(db, "organizations", orgId, "users", newUserId), {
                email: newEmployee.email,
                role: "employee",
                name: newEmployee.name,
                invited: Timestamp.now(),
                invitedBy: user.email,
                directAdd: true
            });

            // Create userOrgs mapping
            await setDoc(doc(db, "userOrgs", newUserId), {
                email: newEmployee.email,
                orgId: orgId,
                role: "employee"
            });

            // Sign out the newly created user (so admin stays logged in)
            const { signOut } = await import("firebase/auth");
            await signOut(auth);

            setDirectAddMessage(`✅ Employee account created successfully!
        
Email: ${newEmployee.email}
Password: ${newEmployee.password}

The employee can now log in directly.`);

            // Clear form
            setNewEmployee({ email: "", name: "", password: "" });

            // Refresh employee list
            const usersRef = collection(db, "organizations", orgId, "users");
            const q = query(usersRef, where("role", "==", "employee"));
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEmployees(data);

        } catch (err) {
            console.error("Error adding employee:", err);
            if (err.code === 'auth/email-already-in-use') {
                setDirectAddMessage("❌ This email is already registered. Try inviting them instead.");
            } else {
                setDirectAddMessage(`❌ Error: ${err.message}`);
            }
        } finally {
            setLoading(false);
        }
    };


    return (
        <>
            {/* Employee Management Buttons */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Employee Management</Typography>
                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddCircleIcon />}
                        onClick={() => setAddEmployeeDialog(true)}
                        sx={{ mr: 2 }}
                    >
                        Add Employee Directly
                    </Button>
                </Box>
            </Box>

            {/* Invite Employee Section */}
            <Box sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                    <PersonAddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Invite New Employee
                </Typography>
                <Box sx={{ display: 'flex', mt: 2 }}>
                    <TextField
                        label="Employee Email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        fullWidth
                        variant="outlined"
                        sx={{ mr: 2 }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleInvite}
                        disabled={loading}
                        startIcon={<HowToRegIcon />}
                    >
                        Send Invite
                    </Button>
                </Box>
                {message && <Alert sx={{ mt: 2 }} severity={message.startsWith("✅") ? "success" : "error"}>{message}</Alert>}
            </Box>

            {/* Employee List */}
            <Typography variant="h6" gutterBottom>Employee List</Typography>
            {loading ? <CircularProgress /> : (
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Email</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Invited/Added</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {employees.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        No employees yet. Invite someone to get started!
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            employees.map(employee => (
                                <TableRow key={employee.id}>
                                    <TableCell>{employee.email}</TableCell>
                                    <TableCell>{employee.name}</TableCell>
                                    <TableCell>
                                        {employee.name === "Pending Invite" ? (
                                            <Chip
                                                label="Pending"
                                                color="warning"
                                                size="small"
                                            />
                                        ) : (
                                            <Chip
                                                label="Active"
                                                color="success"
                                                size="small"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {employee.invited ?
                                            employee.invited.toDate().toLocaleDateString() :
                                            employee.dateAdded ?
                                                employee.dateAdded.toDate().toLocaleDateString() :
                                                "N/A"}
                                    </TableCell>
                                    <TableCell>
                                        {employee.name === "Pending Invite" && (
                                            <Tooltip title="Copy Invite Link">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        const baseUrl = window.location.origin;
                                                        const inviteUrl = `${baseUrl}/invite/${orgId}/${encodeURIComponent(employee.email)}`;
                                                        setInviteLink(inviteUrl);
                                                        setInviteDialogOpen(true);
                                                    }}
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Remove Employee">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteEmployee(employee.id, employee.email)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            )}

            {/* Invite Dialog */}
            <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)}>
                <DialogTitle>Employee Invitation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Share this link with your employee to join the organization:
                    </DialogContentText>
                    <TextField
                        fullWidth
                        value={inviteLink}
                        margin="normal"
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleCopyLink}>
                                        <ContentCopyIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        The employee will be able to create their own password during the registration process.
                    </Typography>
                    {copySuccess && (
                        <Typography variant="caption" color="success.main">
                            {copySuccess}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInviteDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmDialog.open} onClose={() => setDeleteConfirmDialog({ ...deleteConfirmDialog, open: false })}>
                <DialogTitle>Confirm Removal</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {deleteConfirmDialog.isPendingInvite ?
                            `Are you sure you want to cancel the invitation for ${deleteConfirmDialog.email}?` :
                            `Are you sure you want to remove ${deleteConfirmDialog.email} from your organization?`
                        }
                        {!deleteConfirmDialog.isPendingInvite &&
                            " This user will lose access to your organization."
                        }
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmDialog({ ...deleteConfirmDialog, open: false })}>
                        Cancel
                    </Button>
                    <Button onClick={confirmDeleteEmployee} color="error">
                        {deleteConfirmDialog.isPendingInvite ? "Cancel Invitation" : "Remove Employee"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Employee Dialog */}
            <Dialog open={addEmployeeDialog} onClose={() => {
                setAddEmployeeDialog(false);
                setDirectAddMessage("");
                setNewEmployee({ email: "", name: "", password: "" });
            }}>
                <DialogTitle>Add Employee Directly</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Create a new employee account directly without sending an invitation.
                    </DialogContentText>

                    {directAddMessage && (
                        <Alert
                            severity={directAddMessage.startsWith("✅") ? "success" : "error"}
                            sx={{ mt: 2, mb: 2 }}
                        >
                            {directAddMessage}
                        </Alert>
                    )}

                    <TextField
                        label="Email"
                        fullWidth
                        margin="normal"
                        value={newEmployee.email}
                        onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                        required
                    />

                    <TextField
                        label="Name"
                        fullWidth
                        margin="normal"
                        value={newEmployee.name}
                        onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                        required
                    />

                    <TextField
                        label="Password"
                        type="password"
                        fullWidth
                        margin="normal"
                        value={newEmployee.password}
                        onChange={(e) => setNewEmployee({ ...newEmployee, password: e.target.value })}
                        required
                        helperText="Must be at least 6 characters"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setAddEmployeeDialog(false);
                            setDirectAddMessage("");
                            setNewEmployee({ email: "", name: "", password: "" });
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddEmployee}
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : "Add Employee"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default EmployeeManagement;